import React from "react";
import { format } from "date-fns";
import { DATE_FNS_SHORT_DATE_FORMAT, DATE_FNS_LONG_DATE_FORMAT } from "shared/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faRetweet, faUndoAlt, faSitemap, faTimes, faExternalLinkAlt, faExclamation } from "@fortawesome/free-solid-svg-icons";
import { WithIcon, WithTooltip } from "shared/table-helpers";
import { imDropboxContentLink, imDropboxContentLinkText } from "shared/digital-content";
import { CONTEXT_STATUS } from "shared/constants";
import { SHIPMENT_FILTER_TYPES, SHIPMENT_TYPE_MAP } from "components/shipments/lib/constants";
import { inputFilter } from "components/task-list/lib/constants";
import { Select } from "components/form-inputs/inputs/select";
import { DueDateFilterButton } from "components/table/components/dueDateFilterButton";
import { statusBooleanOptions } from "shared/constants/dropdown-options";
import { Checkbox } from "../../../components/form-inputs/inputs/checkbox";
import { Badge, badgeColors } from "../../../components/badge/badge";
import { Tooltip } from "../../../components/tooltip/tooltip";
import { BRAND_VERIFIED_WORKFLOW_TYPE_ID } from "../../../components/shipment-editor/lib/constants";

const CesProductStatus = {
    UNKNOWN: 0,
    ACTIVE: 1,
    ARCHIVED: 2,
    DISCONTINUED: 3,
    NOT_FOUND: 4,
    INTAKE_ONLY: 5,
};

const getStatusColor = (status) => {
    switch (status) {
        case CesProductStatus.DISCONTINUED:
        case CesProductStatus.ARCHIVED:
            return badgeColors.RED;
        case CesProductStatus.NOT_FOUND:
        case CesProductStatus.UNKNOWN:
            return badgeColors.AMBER;
        case CesProductStatus.ACTIVE:
            return badgeColors.PRIMARY;
        default:
            return badgeColors.BLACK;
    }
};

export const hasInvalidProductStatus = (status) =>
    status === CesProductStatus.DISCONTINUED || status === CesProductStatus.ARCHIVED || status === CesProductStatus.UNKNOWN;

export const buildColumns = (
    selectedRows,
    locations,
    updateFinalQaTaskIdSelection,
    selectAllRows,
    repostIntake,
    handleUndoRepostIntake,
    repostGDN,
    handleLinkingManager,
    publishDateMin,
    filterable,
    disableReturnToIntake,
    disablePackageLinkingTool
) => {
    return [
        {
            Header: (props) => {
                const keys = props.data.map((p) => p._original).filter(({ key }) => key);
                const value = selectedRows.length === keys.length && keys.length !== 0;

                return (
                    <Checkbox
                        name="selected-state"
                        value={value}
                        onChange={() => selectAllRows(value ? [] : keys)}
                    />
                );
            },
            accessor: "key",
            filterable: false,
            sortable: false,
            Cell: (row) => {
                const { key } = row.original;
                const keys = selectedRows.map((tableRow) => tableRow.key);

                return (
                    <Checkbox
                        classes="-no-margin"
                        name={`select_${key}`}
                        value={keys.includes(key)}
                        onChange={(e) => updateFinalQaTaskIdSelection(row.original, e.currentTarget.value)}
                    />
                );
            },
            width: 40,
            className: "txt-center",
        },
        {
            Header: WithTooltip("Repost"),
            accessor: "cesProductStatus",
            filterable: false,
            sortable: false,
            style: {
                overflow: "unset",
            },
            Cell: (row) => {
                const status = row.value;
                const { cesProductStatusDescription, key, isFinalized, shipmentKey } = row.original;
                const color = getStatusColor(status);
                const isDisabled = hasInvalidProductStatus(cesProductStatusDescription) || hasInvalidProductStatus(status);

                if (!key || !isFinalized) {
                    return null;
                }

                if (status === CesProductStatus.ACTIVE) {
                    return (
                        <Tooltip
                            position="right"
                            text={cesProductStatusDescription}
                        >
                            <button
                                className="button -text"
                                onClick={() => repostGDN(key, shipmentKey)}
                            >
                                <FontAwesomeIcon
                                    color="#428bca"
                                    icon={faRetweet}
                                />
                            </button>
                        </Tooltip>
                    );
                }

                return (
                    <Badge
                        size="small"
                        color={color}
                        badgeContent={
                            <FontAwesomeIcon
                                style={{
                                    fontSize: "9px",
                                }}
                                color="white"
                                icon={faExclamation}
                            />
                        }
                    >
                        <Tooltip
                            position="right"
                            text={cesProductStatusDescription}
                        >
                            <button
                                className="button -text"
                                onClick={() => repostGDN(key, shipmentKey)}
                                disabled={isDisabled}
                            >
                                <FontAwesomeIcon
                                    className={isDisabled ? "disabled" : ""}
                                    icon={faRetweet}
                                />
                            </button>
                        </Tooltip>
                    </Badge>
                );
            },
            width: 70,
            className: "txt-center",
        },
        {
            Header: WithTooltip("UPC"),
            accessor: "upc",
            Cell: (row) => {
                return row.original.shipmentProduct.modifier ? `${row.value}-${row.original.shipmentProduct.modifier}` : row.value;
            },
            filterable: false,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Publish Date"),
            filterable,
            accessor: "publishDate",
            Filter: ({ filter, onChange }) => {
                return (
                    <DueDateFilterButton
                        filterProp={filter}
                        onChangeProp={onChange}
                        minDate={publishDateMin}
                    />
                );
            },
            Cell: (row) => {
                const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

                return WithTooltip(dateString);
            },
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Package Level"),
            accessor: "packageLevelName",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("CES Client"),
            accessor: "cxHClient",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("GDN Client"),
            accessor: "gdnClient",
            Filter: inputFilter,
            filterable,
        },
        {
            Header: WithTooltip("Location"),
            accessor: "location",
            Cell: (row) => {
                return row.original.hasDigitalContent && row.original.companyName === "ItemMaster" && row.original.location !== "BYOC_Digital" ? (
                    <a
                        href={imDropboxContentLink}
                        alt={imDropboxContentLinkText}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={row.original.location}
                    >
                        {row.original.location}
                    </a>
                ) : (
                    WithTooltip(row.original.location)
                );
            },
            filterable,
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={filter ? filter.value : ""}
                        options={locations}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Shipment Type"),
            accessor: "shipment.type",
            Cell: (row) => {
                const shipmentType = SHIPMENT_TYPE_MAP[row.original.shipment.type].label;

                return WithTooltip(shipmentType);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={SHIPMENT_FILTER_TYPES}
                    />
                );
            },
            //
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Shipment ID"),
            accessor: "shipment.key",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Bin ID"),
            accessor: "binTrackingNumber",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Supplier"),
            accessor: "supplier",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("ISID"),
            accessor: "internalSupplierId",
            Filter: inputFilter,
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Correlation ID"),
            accessor: "correlationId",
            Filter: inputFilter,
            filterable,
            sortable: true,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Updated"),
            accessor: "updatedOn",
            Cell: (row) => {
                const dateString = row.value ? format(new Date(row.value), DATE_FNS_SHORT_DATE_FORMAT) : "";

                return WithTooltip(dateString);
            },
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Due Date"),
            accessor: "shipment.dueDate",
            Cell: (row) => {
                const dateString = row.value ? format(new Date(row.value), DATE_FNS_SHORT_DATE_FORMAT) : "";

                return WithTooltip(dateString);
            },
            filterable: false,
            Aggregated: () => null,
        },
        {
            Header: WithTooltip("Status"),
            accessor: "status",
            Cell: (row) => {
                const status = CONTEXT_STATUS[row.original.status];

                return status ? status.text : row.original.status;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={false}
                        id="select-all-filter"
                        options={statusBooleanOptions}
                    />
                );
            },
            filterable,
            Aggregated: () => null,
        },
        {
            Header: WithIcon("Linking Manager", faSitemap),
            accessor: "shipmentProductKey",
            width: 40,
            Cell: (row) => {
                if (row.original.status === "Rejected") {
                    return null;
                }

                return (
                    <button
                        className="button -text"
                        title="Linking Manager"
                        onClick={() => handleLinkingManager(row)}
                        disabled={disablePackageLinkingTool}
                    >
                        <FontAwesomeIcon icon={faSitemap} />
                    </button>
                );
            },
            filterable: false,
            Aggregated: () => null,
        },
        {
            Header: WithIcon("Return to Intake", faUndoAlt),
            accessor: "key",
            width: 40,
            Cell: (row) => {
                const { hasBeenFinalized, isFinalized, workflowTypeId } = row.original;

                if (workflowTypeId === BRAND_VERIFIED_WORKFLOW_TYPE_ID) {
                    return null;
                }

                if (row.value && hasBeenFinalized && !isFinalized) {
                    return (
                        <button
                            className="button -text"
                            title="Cancel Return to Intake"
                            onClick={() => handleUndoRepostIntake(row.value)}
                            disabled={disableReturnToIntake}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    );
                }

                if (row.value && isFinalized) {
                    return (
                        <button
                            className="button -text"
                            title="Return to Intake: (Alt + Click) for more options"
                            disabled={disableReturnToIntake || hasInvalidProductStatus(row.original.cesProductStatus)}
                            onClick={(e) => {
                                const repostModal = e.altKey;
                                repostIntake(row.value, row.original.shipmentKey, repostModal, row.original.hasSalesSampleFeature);
                            }}
                        >
                            <FontAwesomeIcon icon={faUndoAlt} />
                        </button>
                    );
                }

                return null;
            },
            filterable: false,
            sortable: false,
            Aggregated: () => null,
        },
        {
            Header: WithIcon("Status", faInfoCircle),
            accessor: "shipment.key",
            width: 40,
            Cell: (row) => {
                return row.value ? (
                    <a
                        href={`/shipment-status/${row.value}#${row.original.key}`}
                        title="Shipment Status"
                        className="button -text"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </a>
                ) : (
                    ""
                );
            },
            filterable: false,
            Aggregated: () => null,
        },
        {
            Header: WithIcon("Final QA", faExternalLinkAlt),
            accessor: "finalQaTaskKey",
            width: 40,
            Cell: (row) => {
                return row.value && !row.original.isWipChildProduct ? (
                    <a
                        href={`/final-qa/${row.value}/${row.original.key}`}
                        title="Final QA Task"
                        className="button -text"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                ) : (
                    ""
                );
            },
            filterable: false,
            Aggregated: () => null,
        },
    ];
};
